"use client";

import type { ComponentProps } from "react";
import { Globe } from "lucide-react";

import { MARCAS, type Marca } from "../../brands/brands";
import { cn } from "../../cn";

/**
 * Ícones de rede social — variante web (web e desktop). O par nativo é o
 * `platform-icon.native.tsx`; os traçados das duas saem de `brands`.
 *
 * Cada ícone mantém o nome e a cor padrão que tinha em
 * `apps/web/src/components/ui/icons/` — o Instagram em `text-tami-light`, os
 * outros em `text-foreground` —, porque há telas que dependem desse padrão sem
 * passar `className`.
 */
type SvgProps = ComponentProps<"svg">;

/** As oito marcas de preenchimento: um `path` só, pintado com `currentColor`. */
function IconeDeMarca({
  marca,
  corPadrao = "text-foreground",
  className,
  ...props
}: SvgProps & { readonly marca: Marca; readonly corPadrao?: string }) {
  return (
    <svg
      viewBox={MARCAS[marca].viewBox}
      fill="none"
      className={cn("size-6", corPadrao, className)}
      {...props}
    >
      <path d={MARCAS[marca].d} fill="currentColor" />
    </svg>
  );
}

export function InstagramIcon(props: SvgProps) {
  return <IconeDeMarca marca="instagram" corPadrao="text-tami-light" {...props} />;
}

export function FacebookIcon(props: SvgProps) {
  return <IconeDeMarca marca="facebook" {...props} />;
}

export function LinkedInIcon(props: SvgProps) {
  return <IconeDeMarca marca="linkedin" {...props} />;
}

export function TiktokIcon(props: SvgProps) {
  return <IconeDeMarca marca="tiktok" {...props} />;
}

export function XIcon(props: SvgProps) {
  return <IconeDeMarca marca="x" {...props} />;
}

export function YoutubeIcon(props: SvgProps) {
  return <IconeDeMarca marca="youtube" {...props} />;
}

export function PinterestIcon(props: SvgProps) {
  return <IconeDeMarca marca="pinterest" {...props} />;
}

export function ThreadsIcon(props: SvgProps) {
  return <IconeDeMarca marca="threads" {...props} />;
}

/** O Twitch é o único de contorno — traço, não preenchimento. */
export function TwitchIcon({ className, ...props }: SvgProps) {
  return (
    <svg
      viewBox={MARCAS.twitch.viewBox}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn("size-6 text-foreground", className)}
      {...props}
    >
      <path d={MARCAS.twitch.d}></path>
    </svg>
  );
}

export interface PlatformIconProps {
  /** Nome da rede como vem da API; maiúsculas não importam. */
  readonly platform: string;
  readonly className?: string;
}

/**
 * Ícone pela rede — mesma prop `platform` da variante nativa. Rede
 * desconhecida cai no globo, como lá.
 *
 * O `size-5` padrão é o de `components/platform-icons.tsx` do web, de onde
 * este componente subiu; o ícone solto continua `size-6`.
 */
export function PlatformIcon({ platform, className }: PlatformIconProps) {
  // `text-current`: o ícone pela rede segue a cor do texto em volta. Sem isso
  // o Instagram vinha no `text-tami-light` do ícone solto e sumia no tema
  // claro (selo do Fluxo, card de Campanhas). Cor no `className` ainda vence.
  const classe = cn("size-5 text-current", className);

  switch (platform.toLowerCase()) {
    case "facebook":
      return <FacebookIcon className={classe} />;
    case "instagram":
      return <InstagramIcon className={classe} />;
    case "linkedin":
      return <LinkedInIcon className={classe} />;
    case "threads":
      return <ThreadsIcon className={classe} />;
    case "tiktok":
      return <TiktokIcon className={classe} />;
    case "twitch":
      return <TwitchIcon className={classe} />;
    case "x":
      return <XIcon className={classe} />;
    case "youtube":
      return <YoutubeIcon className={classe} />;
    case "pinterest":
      return <PinterestIcon className={classe} />;
    default:
      return <Globe className={classe} />;
  }
}
